/**
 * לוח בקרה — the analytical-report archetype, and the first screen after sign-in.
 *
 * What it shows depends on the caller's role, and the difference is the point: an owner
 * opens the firm, a bookkeeper opens their own worklist. The alternative — one dashboard
 * with panels greyed out — tells a bookkeeper every morning about numbers they are not
 * allowed to see.
 */
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { AUTHORITIES, formatDate, formatMonthName, type AuthorityKey } from "@onecpa/shared";
import {
  Badge,
  EmptyState,
  ErrorState,
  KpiStrip,
  Money,
  PageHeader,
  Panel,
  StatusBadge,
  TableSkeleton,
} from "@onecpa/ui";
import { api } from "../api/client.js";
import { agorot, minutesToLabel } from "../app/hooks.js";
import { useSession } from "../app/session.js";

export function Dashboard() {
  const session = useSession();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["dashboard", session.orgId],
    queryFn: () => api.analytics.dashboard(session.orgId),
  });

  const { data: exceptions } = useQuery({
    queryKey: ["reporting", "exceptions", session.orgId],
    queryFn: () => api.reporting.exceptions(session.orgId),
  });

  if (error) {
    return <ErrorState message={(error as Error).message} onRetry={() => void refetch()} />;
  }

  const period = data?.period;

  return (
    <>
      <PageHeader
        title="לוח בקרה"
        subtitle={session.orgName}
        period={period ? `${formatMonthName(period.month)} ${period.year}` : undefined}
      />

      {isLoading || !data ? (
        <div className="p-5">
          <TableSkeleton rows={4} columns={5} />
        </div>
      ) : (
        <>
          <KpiStrip
            items={[
              {
                label: "דיווחים החודש",
                value: data.reporting.total,
              },
              {
                label: "ממתינים לחומר",
                value: data.reporting.awaiting,
                tone: data.reporting.awaiting > 0 ? "pending" : "ink",
              },
              {
                label: "באיחור",
                value: data.reporting.late,
                tone: data.reporting.late > 0 ? "ochre" : "ink",
              },
              {
                label: "בסיכון לקנס",
                value: data.reporting.at_risk,
                tone: data.reporting.at_risk > 0 ? "red" : "ink",
              },
              // The money column is absent, not empty, for a bookkeeper. An owner sees
              // the debt; a staff member sees their own hours in its place.
              data.money
                ? {
                    label: "חוב פתוח",
                    value: <Money agorot={agorot(data.money.outstanding)} colorize={false} />,
                    tone: data.money.overdue_count > 0 ? "ochre" : "ledger",
                  }
                : {
                    label: "שעות שלי החודש",
                    value: minutesToLabel(data.minutesThisMonth),
                    tone: "ledger",
                  },
            ]}
          />

          <div className="grid gap-4 p-5 lg:grid-cols-3">
            <Panel title="דורש טיפול" className="lg:col-span-2">
              {exceptions && exceptions.exceptions.length > 0 ? (
                <ul className="divide-y divide-line-soft">
                  {exceptions.exceptions.slice(0, 8).map((row) => (
                    <li key={row.id} className="flex items-center gap-3 px-3.5 py-2.5">
                      <Link
                        to="/clients/$clientId"
                        params={{ clientId: row.clientId }}
                        className="link-underline min-w-0 flex-1 truncate text-sm font-semibold text-ink"
                      >
                        {row.clientName}
                      </Link>
                      {/* The authority, not just the month: a client owes several filings
                          per period, so without it two rows read as duplicates. */}
                      <span className="hidden text-xs text-ink-muted sm:block">
                        {AUTHORITIES[row.authority as AuthorityKey]?.short ?? row.authority} ·{" "}
                        {formatMonthName(row.periodMonth)}
                      </span>
                      <span className="tnum hidden text-xs text-ink-faint md:block">
                        <bdi>{formatDate(row.dueDate)}</bdi>
                      </span>
                      <StatusBadge vocabulary="monthly" status={row.status} />
                    </li>
                  ))}
                </ul>
              ) : (
                <EmptyState
                  title="אין חריגות פתוחות"
                  description="כל הדיווחים לחודש הנוכחי מטופלים."
                  icon="check"
                />
              )}
            </Panel>

            <Panel title="המשימות שלי">
              {data.myTasks.length > 0 ? (
                <ul className="divide-y divide-line-soft">
                  {data.myTasks.map((task) => (
                    <li key={task.id} className="px-3.5 py-2.5">
                      <p className="text-sm text-ink">{task.title}</p>
                      <p className="mt-0.5 flex items-center gap-2 text-2xs text-ink-faint">
                        {task.clientName && <span className="truncate">{task.clientName}</span>}
                        {task.dueDate && <bdi className="tnum">{formatDate(task.dueDate)}</bdi>}
                        {task.priority === "urgent" && <Badge tone="danger">דחוף</Badge>}
                      </p>
                    </li>
                  ))}
                </ul>
              ) : (
                <EmptyState title="אין משימות פתוחות" icon="check" />
              )}
            </Panel>

            <Panel title="מועדים קרובים" className="lg:col-span-2">
              {data.upcoming.length > 0 ? (
                <ul className="divide-y divide-line-soft">
                  {data.upcoming.map((entry) => (
                    <li key={entry.id} className="flex items-center gap-3 px-3.5 py-2.5">
                      <span className="min-w-0 flex-1 truncate text-sm text-ink">
                        {entry.title}
                      </span>
                      {entry.estimatedHours ? (
                        <span className="tnum text-2xs text-ink-faint">
                          {entry.estimatedHours} ש׳
                        </span>
                      ) : null}
                      <bdi className="tnum text-xs text-ink-muted">
                        {formatDate(entry.dueDate)}
                      </bdi>
                    </li>
                  ))}
                </ul>
              ) : (
                <EmptyState title="אין מועדים בטווח הקרוב" icon="calendar-check" />
              )}
            </Panel>

            <Panel title="תיקים">
              <dl className="divide-y divide-line-soft">
                <Row label="ממתינים ללקוח" value={data.cases.blocked} tone="ochre" />
                <Row label="מוכנים לעבודה" value={data.cases.ready} tone="ledger" />
                <Row label="בטיפול" value={data.cases.open} />
                <Row
                  label="משימות באיחור"
                  value={data.tasks.overdue}
                  {...(data.tasks.overdue > 0 ? { tone: "red" as const } : {})}
                />
              </dl>
            </Panel>
          </div>
        </>
      )}
    </>
  );
}

function Row({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: "ledger" | "ochre" | "red";
}) {
  const color =
    tone === "red"
      ? "text-red-ink"
      : tone === "ochre"
        ? "text-ochre"
        : tone === "ledger"
          ? "text-ledger"
          : "text-ink";
  return (
    <div className="flex items-center justify-between px-3.5 py-2">
      <dt className="text-xs text-ink-muted">{label}</dt>
      <dd className={`serif tnum text-lg ${color}`}>{value}</dd>
    </div>
  );
}
